import React from "react";

/**
 * KenBurnsShot — a single screenshot with a subtle zoom/pan animation.
 * Optional `hotspot` (JSX) is layered above the image for scene-specific
 * overlays (e.g. the simulated 1-click Implement + ticker jump in scene 3
 * or the Approve-all-safe pill flash in scene 5).
 */
export const KenBurnsShot = ({ src, alt, from = "left", to = "right", zoom = 1.08, hotspot }) => {
  // Two keyframe presets: "left→right" and "center-in"
  const xFrom = from === "left" ? "-2%" : from === "right" ? "2%" : "0";
  const xTo   = to   === "left" ? "-2%" : to   === "right" ? "2%" : "0";
  return (
    <div className="relative w-full h-full overflow-hidden bg-slate-100">
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          animation: `kb-pan 9000ms ease-in-out forwards`,
          transformOrigin: "center center",
          ["--kb-x-from"]: xFrom,
          ["--kb-x-to"]: xTo,
          ["--kb-zoom"]: zoom,
        }}
      />
      {/* subtle vignette top + bottom to keep captions readable */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24"
        style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.35), transparent)" }}
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36"
        style={{ background: "linear-gradient(0deg, rgba(15,23,42,0.55), transparent)" }}
      />
      {hotspot}
    </div>
  );
};

/**
 * SceneCaption — bottom-left caption + optional kicker (scene number).
 */
export const SceneCaption = ({ kicker, headline, accent, sub }) => (
  <div
    className="absolute left-4 right-4 bottom-6 sm:left-8 sm:right-8 sm:bottom-8 z-20 text-white"
    style={{ animation: "cap-in 700ms cubic-bezier(.2,.8,.2,1) 200ms both" }}
  >
    {kicker && (
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/95 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg shadow-emerald-500/30 mb-2">
        {kicker}
      </div>
    )}
    <h2 className="text-lg sm:text-2xl md:text-3xl font-extrabold leading-tight max-w-3xl drop-shadow-lg">
      {headline} {accent && <span className="text-emerald-300">{accent}</span>}
    </h2>
    {sub && (
      <p className="mt-1 text-white/85 text-xs sm:text-sm md:text-base max-w-2xl leading-snug">
        {sub}
      </p>
    )}
  </div>
);

export default KenBurnsShot;
